import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

/**
 * How many artifacts one chat may be started from.
 *
 * Every chip is a document the model will likely read, so this bounds the
 * opening turn rather than the URL. Ten is a working ceiling, not a measured
 * one: the artifact inventory in the system prompt already lists up to 50 by
 * name, but those are names, and these are full reads.
 */
export const MAX_CHAT_START_ARTIFACTS = 10;

/**
 * Hook for navigating to the home page with artifacts pre-selected.
 * The project is derived from the artifacts' space, and the chat is
 * created lazily when the user sends their first message.
 */
export function useArtifactNavigation() {
	const navigate = useNavigate();

	const openArtifactInChat = useCallback(
		(artifactId: string, _spaceId?: string) => {
			void navigate({
				to: "/home",
				search: { artifactId },
			});
		},
		[navigate],
	);

	/**
	 * Start one chat from several artifacts.
	 *
	 * Exactly one artifact goes down the existing single path deliberately. That
	 * path pins the artifact as ACTIVE, which binds "the artifact" in the prompt
	 * to it and opens it in the side panel — behaviour worth keeping for the
	 * common case, and behaviour that has no meaning for a set (see
	 * `buildArtifactsSection`: there is no "the artifact" among five).
	 *
	 * Two or more become reference chips in the composer instead.
	 */
	const openArtifactsInChat = useCallback(
		(artifactIds: string[]) => {
			if (artifactIds.length === 0) {
				return;
			}
			if (artifactIds.length === 1) {
				openArtifactInChat(artifactIds[0]);
				return;
			}
			void navigate({
				to: "/home",
				search: { artifactIds: artifactIds.slice(0, MAX_CHAT_START_ARTIFACTS) },
			});
		},
		[navigate, openArtifactInChat],
	);

	return { openArtifactInChat, openArtifactsInChat, isNavigating: false };
}
